/**
 * Filtri specializzati per date e range di date
 *
 * @description
 * Questo modulo contiene i filtri per la gestione di date singole e range di date.
 * - DateFilter: per date singole con formatting automatico
 * - DateRangeFilter: per range di date (da/a) con serializzazione speciale
 *
 * Utilizza date-fns per la formattazione e gestisce automaticamente
 * la conversione tra stringhe ISO, oggetti Date e formati personalizzati.
 */

import { format } from 'date-fns';
import { BaseFilter } from '../core/base-filter';
import type { QueryParams } from '../types';

/**
 * Interfaccia per dati di filtro range date
 * Utilizzabile per Vue 3 Composition API con oggetti plain reattivi
 */
export interface IDateRangeFilterData {
	/** Nome della proprietà da filtrare */
	propertyName: string;
	/** Valore corrente del filtro (array di 2 date) */
	value: Date[] | null;
	/** Valore di default del filtro */
	defaultValue: Date[] | null;
	/** Nome della proprietà nella query string */
	queryPropertyName: string;
	/** Operatore di ricerca applicato */
	searchOperator: string;
	/** Se aggiornare la query string al cambio */
	updateQueryOnChange: boolean;
	/** Se serializzare solo la parte data (no orario) */
	onlyDatePart: boolean;
}

/**
 * Tipo combinato per filtro range date con metodi fluenti
 */
export type DateRangeFilterFactory = IDateRangeFilterData & {
	/** Imposta il valore iniziale */
	initialValue(value: Date[] | null): DateRangeFilterFactory;
	/** Imposta il valore di default */
	default(value: Date[] | null): DateRangeFilterFactory;
	/** Configura aggiornamento query string */
	updateQueryOnFilterChange(value: boolean): DateRangeFilterFactory;
	/** Configura nome proprietà query string */
	setQueryPropertyName(value: string): DateRangeFilterFactory;
	/** Operatore "tra" (range di date) */
	between(): DateRangeFilterFactory;
	/** Configura se usare solo la parte data */
	setOnlyDatePart(value: boolean): DateRangeFilterFactory;
};

/**
 * Filtro per singole date
 *
 * @description
 * DateFilter gestisce date singole con supporto per diversi formati di output.
 * Può restituire solo la parte data (YYYY-MM-DD) o l'ISO completo con orario.
 *
 * @example
 * // Filtro per data di creazione (solo data)
 * const createdFilter = new DateFilter('createdAt', true)
 *   .greaterThanOrEqual()    // Data >= valore
 *   .initialValue(new Date('2023-01-01'));
 *
 * @example
 * // Filtro per timestamp completo
 * const timestampFilter = new DateFilter('lastModified')
 *   .between()               // Tra due date
 *   .queryPropertyName('modified');
 *
 * @category Filters
 * @since 1.0.0
 */
export class DateFilter extends BaseFilter<Date | null> {
	/**
	 * Se true, serializza solo la parte data (YYYY-MM-DD)
	 * Se false, serializza l'ISO completo con orario
	 */
	public onlyDatePart: boolean = false;

	/**
	 * Crea una nuova istanza di DateFilter
	 *
	 * @param propertyName - Nome della proprietà da filtrare
	 * @param onlyDatePart - Se true, usa solo la parte data (default: false)
	 *
	 * @example
	 * // Filtro per data completa con orario
	 * const filter = new DateFilter('timestamp');
	 *
	 * @example
	 * // Filtro per solo data (senza orario)
	 * const filter = new DateFilter('birthDate', true);
	 *
	 * @public
	 */
	constructor(propertyName: string, onlyDatePart: boolean = false) {
		super(Date, propertyName);
		this.onlyDatePart = onlyDatePart;
	}

	/**
	 * Deserializza il valore dalla query string
	 * Converte stringhe ISO in oggetti Date
	 *
	 * @param query - Parametri della query string
	 *
	 * @example
	 * // URL: ?created_at=2023-01-01
	 * filter._fromQuery({ created_at: '2023-01-01' });
	 * // filter.value diventa new Date('2023-01-01')
	 *
	 * @example
	 * // URL: ?timestamp=2023-01-01T10:30:00Z
	 * filter._fromQuery({ timestamp: '2023-01-01T10:30:00Z' });
	 * // filter.value diventa new Date('2023-01-01T10:30:00Z')
	 *
	 * @protected
	 * @override
	 */
	protected _fromQuery(query: QueryParams): void {
		const value = query[this._queryPropertyName];
		if (value !== undefined) {
			// FIX: Gestisci 'null' letterale dalla query string
			if (value === 'null') {
				this.value = null;
				return;
			}

			if (!value) {
				this.value = this._defaultValue || null;
			} else {
				try {
					// Converte la stringa in oggetto Date
					this.value = new Date(String(value));

					// Verifica che sia una data valida
					if (isNaN(this.value.getTime())) {
						console.warn(`DateFilter: Invalid date string "${value}"`);
						this.value = this._defaultValue || null;
					}
				} catch (error) {
					console.error('❌ ERROR date-filters.Date:', error);
					console.warn(`DateFilter: Error parsing date "${value}":`, error);
					this.value = this._defaultValue || null;
				}
			}
		}
	}

	/**
	 * Serializza la data per la query string
	 * Usa il formato specificato da onlyDatePart
	 *
	 * @returns Data formattata come stringa
	 *
	 * @example
	 * // Con onlyDatePart = true
	 * filter.value = new Date('2023-01-01T10:30:00');
	 * filter._getSerializedValue(); // '2023-01-01'
	 *
	 * @example
	 * // Con onlyDatePart = false
	 * filter.value = new Date('2023-01-01T10:30:00');
	 * filter._getSerializedValue(); // '2023-01-01T10:30:00.000Z'
	 *
	 * @protected
	 * @override
	 */
	protected _getSerializedValue(): string | undefined {
		if (!this.value || isNaN(this.value.getTime())) {
			return undefined;
		}

		try {
			if (this.onlyDatePart) {
				// Restituisce solo la parte data nel formato ISO (YYYY-MM-DD)
				return format(this.value, 'yyyy-MM-dd');
			} else {
				// Restituisce l'ISO completo con orario
				return this.value.toISOString();
			}
		} catch (error) {
			console.error('❌ ERROR date-filters.if:', error);
			console.warn('DateFilter: Error serializing date:', error);
			return undefined;
		}
	}

	/**
	 * Verifica se il valore è quello di default
	 * Gestisce date nulle o invalide
	 *
	 * @returns true se il valore è di default, false altrimenti
	 *
	 * @protected
	 * @override
	 */
	protected _valueIsDefault(): boolean {
		return (
			this.value === undefined ||
			this.value === null ||
			(this.value instanceof Date && isNaN(this.value.getTime())) ||
			this.value === this._defaultValue
		);
	}
}

/**
 * Filtro per range di date
 *
 * @description
 * DateRangeFilter gestisce coppie di date (inizio/fine) per operazioni di range.
 * Serializza le date come stringa separata da '*' e supporta sia formato solo-data
 * che timestamp completi.
 *
 * @example
 * // Filtro per periodo di fatturazione
 * const billingPeriodFilter = new DateRangeFilter('billingDate', true)
 *   .between()               // WHERE billingDate BETWEEN start AND end
 *   .initialValue([
 *     new Date('2023-01-01'),
 *     new Date('2023-01-31')
 *   ]);
 *
 * @example
 * // Filtro per timestamp range
 * const sessionFilter = new DateRangeFilter('sessionTime')
 *   .between()
 *   .queryPropertyName('sessionRange');
 *
 * @category Filters
 * @since 1.0.0
 */
export class DateRangeFilter extends BaseFilter<Date[] | null> {
	/**
	 * Se true, serializza solo la parte data (YYYY-MM-DD)
	 * Se false, serializza l'ISO completo con orario
	 */
	public onlyDatePart: boolean = false;

	/**
	 * Crea una nuova istanza di DateRangeFilter
	 *
	 * @param propertyName - Nome della proprietà da filtrare
	 * @param onlyDatePart - Se true, usa solo la parte data (default: false)
	 *
	 * @example
	 * // Range per timestamp completi
	 * const filter = new DateRangeFilter('createdAt');
	 *
	 * @example
	 * // Range per sole date (senza orario)
	 * const filter = new DateRangeFilter('eventDate', true);
	 *
	 * @public
	 */
	constructor(propertyName: string, onlyDatePart: boolean = false) {
		super(Date, propertyName);
		this.onlyDatePart = onlyDatePart;
	}

	/**
	 * Deserializza il range dalla query string
	 * Converte stringhe separate da '*' in array di Date
	 *
	 * @param query - Parametri della query string
	 *
	 * @example
	 * // URL: ?date_range=2023-01-01*2023-01-31
	 * filter._fromQuery({ date_range: '2023-01-01*2023-01-31' });
	 * // filter.value diventa [new Date('2023-01-01'), new Date('2023-01-31')]
	 *
	 * @example
	 * // URL: ?timestamps=2023-01-01T00:00:00Z*2023-01-31T23:59:59Z
	 * filter._fromQuery({ timestamps: '2023-01-01T00:00:00Z*2023-01-31T23:59:59Z' });
	 * // filter.value diventa array di due Date objects
	 *
	 * @protected
	 * @override
	 */
	protected _fromQuery(query: QueryParams): void {
		const value = query[this._queryPropertyName];
		if (value !== undefined) {
			// FIX: Gestisci 'null' letterale dalla query string
			if (value === 'null') {
				this.value = null;
				return;
			}

			if (!value) {
				this.value = this._defaultValue || null;
			} else {
				try {
					const stringValue = String(value);
					const tokens = stringValue.split('*');

					if (tokens.length >= 2) {
						// Converte entrambe le stringhe in Date
						const startDate = new Date(tokens[0].trim());
						const endDate = new Date(tokens[1].trim());

						// Verifica che entrambe le date siano valide
						if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
							this.value = [startDate, endDate];
						} else {
							console.warn(`DateRangeFilter: Invalid date range "${value}"`);
							this.value = this._defaultValue || null;
						}
					} else {
						console.warn(`DateRangeFilter: Invalid range format "${value}". Expected: start*end`);
						this.value = this._defaultValue || null;
					}
				} catch (error) {
					console.error('❌ ERROR date-filters.Date:', error);
					console.warn(`DateRangeFilter: Error parsing date range "${value}":`, error);
					this.value = this._defaultValue || null;
				}
			}
		}
	}

	/**
	 * Serializza il range di date per la query string
	 * Converte l'array in stringa con separatore '*'
	 *
	 * @returns Range serializzato come stringa
	 *
	 * @example
	 * // Con onlyDatePart = true
	 * filter.value = [new Date('2023-01-01'), new Date('2023-01-31')];
	 * filter._getSerializedValue(); // '2023-01-01*2023-01-31'
	 *
	 * @example
	 * // Con onlyDatePart = false
	 * filter.value = [new Date('2023-01-01T00:00:00'), new Date('2023-01-01T23:59:59')];
	 * filter._getSerializedValue(); // '2023-01-01T00:00:00.000Z*2023-01-01T23:59:59.000Z'
	 *
	 * @protected
	 * @override
	 */
	protected _getSerializedValue(): string | undefined {
		if (!this.value || !Array.isArray(this.value) || this.value.length < 2) {
			return undefined;
		}

		try {
			const [startDate, endDate] = this.value;

			// Verifica che entrambe le date siano valide
			if (!startDate || !endDate || isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
				return undefined;
			}

			if (this.onlyDatePart) {
				// Serializza solo le parti data
				const startStr = format(startDate, 'yyyy-MM-dd');
				const endStr = format(endDate, 'yyyy-MM-dd');
				return `${startStr}*${endStr}`;
			} else {
				// Serializza ISO completi
				return `${startDate.toISOString()}*${endDate.toISOString()}`;
			}
		} catch (error) {
			console.error('❌ ERROR date-filters.if:', error);
			console.warn('DateRangeFilter: Error serializing date range:', error);
			return undefined;
		}
	}

	/**
	 * Verifica se il valore è quello di default
	 * Considera array vuoti o con date invalide come default
	 *
	 * @returns true se il valore è di default, false altrimenti
	 *
	 * @protected
	 * @override
	 */
	protected _valueIsDefault(): boolean {
		if (this.value === undefined || this.value === null || this.value === this._defaultValue) {
			return true;
		}

		if (!Array.isArray(this.value) || this.value.length < 2) {
			return true;
		}

		// Verifica che entrambe le date siano valide
		const [startDate, endDate] = this.value;
		return !startDate || !endDate || isNaN(startDate.getTime()) || isNaN(endDate.getTime());
	}

	/**
	 * Override del metodo _setContext per gestire array vuoti o date invalide
	 * Non aggiunge il filtro al contesto se non ha un range valido
	 *
	 * @param context - Contesto OData da modificare
	 *
	 * @protected
	 * @override
	 */
	protected _setContext(context: any): void {
		// Non aggiunge il filtro se il range non è valido
		if (!this.value || !Array.isArray(this.value) || this.value.length < 2) {
			return;
		}

		const [startDate, endDate] = this.value;
		if (!startDate || !endDate || isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
			return;
		}

		// Delega alla implementazione della classe base
		super._setContext(context);
	}
}

/**
 * Factory function per creare filtri range date tipizzati
 * Compatibile con Vue 3 Composition API e completamente reattivo
 *
 * @param propertyName - Nome della proprietà da filtrare
 * @param onlyDatePart - Se true, usa solo la parte data (default: false)
 * @returns Oggetto filtro range date tipizzato con API fluente
 *
 * @example
 * // Per Vue 3 Composition API
 * const dateRangeFilter = ref(createDateRangeFilterData('createdAt').between());
 *
 * @example
 * // Con configurazione completa
 * const eventDateFilter = ref(
 *   createDateRangeFilterData('eventDate', true)
 *     .between()
 *     .initialValue([new Date('2023-01-01'), new Date('2023-12-31')])
 *     .updateQueryOnFilterChange(true)
 * );
 */
export function createDateRangeFilterData(
	propertyName: string,
	onlyDatePart: boolean = false,
): DateRangeFilterFactory {
	// Sanitizza nome proprietà per query string
	const sanitizedName = propertyName.replace(/[.[\\]]/g, '_').toLowerCase();

	// Oggetto combinato con dati e metodi
	const filter: DateRangeFilterFactory = {
		propertyName,
		value: null,
		defaultValue: null,
		queryPropertyName: sanitizedName,
		searchOperator: 'NOOP',
		updateQueryOnChange: false,
		onlyDatePart,

		initialValue(value: Date[] | null): DateRangeFilterFactory {
			filter.value = value;
			return filter;
		},

		default(value: Date[] | null): DateRangeFilterFactory {
			filter.defaultValue = value;
			return filter;
		},

		updateQueryOnFilterChange(value: boolean): DateRangeFilterFactory {
			filter.updateQueryOnChange = value;
			return filter;
		},

		setQueryPropertyName(value: string): DateRangeFilterFactory {
			filter.queryPropertyName = value;
			return filter;
		},

		between(): DateRangeFilterFactory {
			filter.searchOperator = 'BETWEEN';
			return filter;
		},

		setOnlyDatePart(value: boolean): DateRangeFilterFactory {
			filter.onlyDatePart = value;
			return filter;
		},
	};

	return filter;
}
